'use client'

import React, { useState, useEffect, useCallback } from 'react'
import Link from 'next/link'
import clsx from 'clsx'
import { ERROR_MESSAGES } from '@/constants/messages'
import { TopAllResponse } from '@/lib/api/types/api'
import { Category } from '@/types/category'
import { Tag } from '@/types/tag'
import styles from './styles.module.scss'
import { useSelector } from 'react-redux'
import { RootState } from '@/store'
import { HomeService } from '@/lib/api/services/home'
import { CategoryService } from '@/lib/api/services/category'
import Loading from '@/components/Shared/Loading'
import TitleWithViewAll from '@/components/Shared/Title/TitleWithViewAll'
import CarouselContents from '@/components/Shared/CarouselContents'
import TopCarousel from '@/components/Shared/TopCarousel'
import ArticleCard from '@/components/Shared/Card/ArticleCard'
import SearchInputSuggestions from '@/components/Shared/SearchInputSuggestions'
import RecommendedContents from '@/components/Shared/RecommendedContents'
import CookiePolicyDialog from '@/components/Shared/CookiePolicyDialog'
import { useAuth } from '@/hooks/useAuth'
import RoleModelFinderBanner from '@/components/Shared/RoleModelFinderBanner'
import MemberRegistration from '@/components/Shared/MemberRegistration'
import DataFetchError from '@/components/Shared/DataFetchError'
import { ApiError } from '@/utility/apiClient'
import { SESSION_STORAGE_KEYS, COOKIE_KEYS } from '@/constants/storageKeys'

// キービジュアル用のSVG画像パス（実際のアセットに応じて調整）
const KV_LEAD_SVG = '/images/svg/kv-lead.svg'

// title
const titleWords = ['なりたい自分が', '見つかる。']

// 検索推奨ワード（コンポーネント外で定義）
const searchRecommendWords = [
  '銀行',
  '自動車',
  '製薬',
  '建設',
  '薬局',
  '保険',
  '不動産',
  '物流',
  '施工管理',
  '薬剤師',
  'インターン',
  '女性 SE',
  '女性 管理職',
  '生産管理',
]

// カテゴリ表示用のID順序
const targetCategoryIds = [300, 236, 280, 296, 321, 228, 334, 324, 279, 160, 237, 124, 16, 36]

// カテゴリ分類
const _categoryList = [
  { id: 1, name: 'キャリアパス', order: 2 },
  { id: 2, name: 'カルチャー', order: 7 },
  { id: 3, name: '制度', order: 6 },
  { id: 4, name: '創業・起業', order: 8 },
  { id: 5, name: '事業・経営', order: 5 },
  { id: 8, name: '社会', order: 9 },
  { id: 9, name: 'その他', order: 10 },
  { id: 10, name: '職種', order: 1 },
  { id: 11, name: '働き方', order: 4 },
  { id: 12, name: '仕事への思い', order: 3 },
]

// 会員限定コンテンツ
const memberOnlyContents = {
  contents: [
    {
      icon: 'add-user.svg',
      pointImg: 'point01.svg',
      text: '企業・カテゴリをフォローして新着コンテンツを確認！',
    },
    {
      icon: 'good.svg',
      pointImg: 'point02.svg',
      text: 'あなたの興味や関心から、おすすめ企業やカテゴリをレコメンド',
    },
    {
      icon: 'heart.svg',
      pointImg: 'point03.svg',
      text: 'お気に入りやあとで読みたいコンテンツをマイ本棚に保存！',
    },
  ],
}

export default function HomePageClient() {
  const [payload, setPayload] = useState<TopAllResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [randomSearchWords, setRandomSearchWords] = useState<string[]>([])
  const [showRecommend, setShowRecommend] = useState(true)
  const [isOpenCookiePolicyDialog, setIsOpenCookiePolicyDialog] = useState(false)
  const [extractedCategoryList, setExtractedCategoryList] = useState<Category[]>([])
  const [isMounted, setIsMounted] = useState(false)
  const { getMemberId } = useAuth()
  const isUserLoading = useSelector((state: RootState) => state.user.isLoading)

  // ランダム配列生成
  const randomArray = useCallback((array: string[]): string[] => {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }, [])

  // カテゴリミックスのタイトル生成
  const categoryMixTitle = useCallback((tags: Tag[]): string => {
    return tags.map((tag) => tag.name).join(' + ')
  }, [])

  // カテゴリミックスのリンク生成
  const categoryMixLink = useCallback((tags: Tag[]): string => {
    return '/contents?category=' + tags.map((tag) => tag.id).join(',')
  }, [])

  // レコメンド処理（Nuxtバージョンに合わせて）
  const handleRecommendation = useCallback((recommendAi: unknown[]): void => {
    setShowRecommend(recommendAi.length > 0)
  }, [])

  // getImagePath関数（SVG画像パスを取得）
  const getImagePath = useCallback((path: string): string => {
    return path ? `/images/svg/${path}` : ''
  }, [])

  // データ取得（リトライ機能付き）
  useEffect(() => {
    let isMounted = true
    let retryTimeoutId: ReturnType<typeof setTimeout>

    const fetchData = async (retryCount = 0) => {
      try {
        const [data, allTags] = await Promise.all([
          HomeService.getTopAll(),
          CategoryService.getAllTags(),
        ])

        // Only update state if component is still mounted
        if (isMounted) {
          setPayload(data)

          // Extract categories based on targetCategoryIds
          const extractedCategories = targetCategoryIds.reduce((acc: Category[], id) => {
            const foundTag = allTags.find((tag: Tag) => tag.id === id)
            if (foundTag) {
              acc.push({
                id: foundTag.id,
                name: foundTag.name,
                slug: foundTag.slug,
                categoryId: foundTag.categoryId,
              })
            }
            return acc
          }, [])

          setExtractedCategoryList(extractedCategories)
          setError(null) // Clear any previous errors
        }
      } catch (err) {
        const error = err as Error

        // Only handle errors if component is still mounted
        if (!isMounted) return

        // Network errors or timeouts - retry up to 2 times
        if (
          (error.message.includes('Network Error') ||
            error.message.includes('timeout') ||
            error.message.includes('Failed to fetch')) &&
          retryCount < 2
        ) {
          console.warn(`HomeService.getTopAll() failed, retrying... (${retryCount + 1}/2)`)
          retryTimeoutId = setTimeout(
            () => {
              if (isMounted) {
                fetchData(retryCount + 1)
              }
            },
            1000 * (retryCount + 1),
          ) // Exponential backoff
          return
        }

        // Don't show error for abort errors (user navigated away quickly)
        if (error.name === 'AbortError' || error.message.includes('aborted')) {
          setLoading(false)
          return
        }

        // APIエラーのログ記録
        if (err instanceof ApiError) {
          console.error(`HomeService.getTopAll() API error [${err.status}]:`, err.message)
        } else {
          console.error('HomeService.getTopAll() error:', error)
        }
        setError(error.message)
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchData()

    // Cleanup function
    return () => {
      isMounted = false
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId)
      }
    }
  }, [])

  // マウント状態を設定（Hydration エラー対策）
  useEffect(() => {
    setIsMounted(true)
  }, [])

  // ランダム検索ワード設定
  useEffect(() => {
    if (typeof window === 'undefined') return // SSR時はスキップ

    const storageData = sessionStorage.getItem(SESSION_STORAGE_KEYS.RANDOM_SEARCH_WORDS)
    const currentTime = new Date().getTime()

    if (storageData) {
      try {
        const parsedData = JSON.parse(storageData)
        // 型チェック: createdAtとwordsが正しい形式か確認
        if (
          parsedData &&
          typeof parsedData === 'object' &&
          typeof parsedData.createdAt === 'number' &&
          Array.isArray(parsedData.words)
        ) {
          // 10分（600,000ms）以内ならstorageの値を使用
          if (currentTime - parsedData.createdAt < 600000) {
            setRandomSearchWords(parsedData.words)
            return
          }
        } else {
          // 不正なデータ形式の場合は削除
          sessionStorage.removeItem(SESSION_STORAGE_KEYS.RANDOM_SEARCH_WORDS)
        }
      } catch (error) {
        console.error('Failed to parse stored randomSearchWords:', error)
        // パース失敗時は古いデータを削除
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.RANDOM_SEARCH_WORDS)
      }
    }

    // 新しいランダムワードを生成
    const randomWords = randomArray([...searchRecommendWords]).slice(0, 3)
    try {
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.RANDOM_SEARCH_WORDS,
        JSON.stringify({ words: randomWords, createdAt: currentTime }),
      )
    } catch (error) {
      console.error('Failed to store randomSearchWords:', error)
    }
    setRandomSearchWords(randomWords)
  }, [randomArray])

  // Cookie Policy Dialog 設定
  useEffect(() => {
    if (typeof window === 'undefined') return // SSR時はスキップ

    // クッキーポリシーダイアログの表示制御
    const isReadCookiePolicy = document.cookie.includes(COOKIE_KEYS.READ_COOKIE_POLICY)
    setIsOpenCookiePolicyDialog(!isReadCookiePolicy)
  }, [])

  if (loading) return <Loading />

  if (error) {
    return (
      <DataFetchError
        title={ERROR_MESSAGES.GENERIC}
        message={ERROR_MESSAGES.LOADING('ホームページ')}
        errorDetails={error}
        used='home'
      />
    )
  }

  if (!payload) return <Loading />

  return (
    <div className={'wrapper'}>
      <div className={styles.homeContainer}>
        {/* First Section - Key Visual */}
        <div className={styles.firstSectionWrapper}>
          <div className={styles.firstSection}>
            <div className={styles.firstSectionInner}>
              <div className={styles.kvLead}>
                <img
                  src={KV_LEAD_SVG}
                  width={107}
                  height={36}
                  alt='10,000'
                  className={styles.kvLeadImg}
                  loading='lazy'
                />
                <span className={styles.kvLeadText}>のロールモデルから</span>
              </div>
              <h1 className={styles.kvTitle}>
                {titleWords.map((word, wordIndex) => (
                  <React.Fragment key={wordIndex}>
                    <span className={styles.decorationGroup} aria-hidden='true'>
                      {word.split('').map((char, index) => (
                        <span
                          className={clsx(styles.decoItem, styles[`decoItem-v${index}`])}
                          key={index}
                        >
                          {char}
                        </span>
                      ))}
                    </span>
                    <span className={styles.titleText} aria-hidden='true'>
                      {word}
                    </span>
                  </React.Fragment>
                ))}
                <span className={styles.titleTextSp}>{titleWords.join('')}</span>
              </h1>
              <div className={styles.searchWrapper}>
                <div className={styles.searchInner}>
                  <div className={styles.searchText}>キーワードで探す</div>
                  <div className={styles.searchInputWrapper}>
                    <SearchInputSuggestions />
                    <ul className={styles.searchWordList}>
                      {isMounted &&
                        randomSearchWords.map((word, index) => (
                          <li key={index} className={styles.searchWordItem}>
                            <Link
                              href={`/search?query=${word}`}
                              className={clsx('textLink', styles.inline)}
                            >
                              {word}
                            </Link>
                          </li>
                        ))}
                      <li className={styles.searchWordItem}>
                        <Link
                          href='/talentbook_guide'
                          className={clsx('textLink', styles.inline, styles.spNonDeco)}
                        >
                          🔰はじめての方へ
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
                <RoleModelFinderBanner from='top' />
              </div>
            </div>
          </div>
        </div>

        {/* Member Registration */}
        {!isUserLoading && <MemberRegistration />}

        {/* Recommend Carousel */}
        <div className={styles.recommendCarousel}>
          <TopCarousel items={payload.recommendedContents} type='article' />
        </div>

        {/* Top Companies Section */}
        {payload.topCompanies && payload.topCompanies.length > 0 && (
          <div className={clsx(styles.companiesWrapper, styles.sectionContents)}>
            <section className={clsx(styles.contentWidth, styles.companiesSection)}>
              <TitleWithViewAll path='/companies' title='企業から探す' />
              <div className={clsx('cardContainer', styles.companiesCards)}>
                <TopCarousel items={payload.topCompanies} type='companies' />
              </div>
            </section>
          </div>
        )}

        {/* Pickup Section */}
        {payload.pickup && payload.pickup.length > 0 && (
          <div className={clsx(styles.adPickupWrapper, styles.sectionContents, styles.grayBg)}>
            <section className={clsx(styles.contentWidth, styles.adPickupSection)}>
              <a href={payload.pickup[0].linkUrl} target='_blank' rel='noopener noreferrer'>
                <dl className={styles.adPickupContent}>
                  <dt className={styles.adPickupImageArea}>
                    <img
                      src={payload.pickup[0].eyecatchImageUrl}
                      width={800}
                      height={450}
                      className={styles.adPickupImage}
                      alt={payload.pickup[0].title}
                      loading='lazy'
                    />
                  </dt>
                  <dd className={styles.adPickupText}>
                    <h3 className={styles.adPickupTitle}>{payload.pickup[0].title}</h3>
                    <p className={styles.adPickupDescription}>{payload.pickup[0].intro}</p>
                    <p
                      className={clsx(styles.adPickupName, {
                        [styles.adIcon]: payload.pickup[0].hasAdLabel,
                      })}
                    >
                      {payload.pickup[0].company.name}
                    </p>
                  </dd>
                </dl>
              </a>
            </section>
          </div>
        )}

        {/* Main Contents Wrapper */}
        <div className={styles.mainContentsWrapper}>
          {/* Recommended Section */}
          {showRecommend && (
            <div className={clsx(styles.designWrapper, styles.sectionOuterMargin)}>
              <i className={clsx(styles.gradientCircle, styles.yellow, styles.iconPos1)} />
              <i className={clsx(styles.gradientCircle, styles.yellow, styles.iconPos2)} />
              <div className={clsx(styles.recommendedWrapper, styles.sectionSpacing)}>
                <section className={clsx(styles.contentWidth, styles.recommendedSection)}>
                  <TitleWithViewAll title='あなたへのおすすめ' viewAll={false} />
                  <RecommendedContents
                    used='top-recommend'
                    onRecommendationResponse={handleRecommendation}
                  />
                </section>
              </div>
            </div>
          )}

          {/* Banners Section */}
          {payload.banners && payload.banners.length > 0 && (
            <div className={clsx(styles.bannersWrapper, styles.sectionContents)}>
              <section className={clsx(styles.contentWidth, styles.bannersSection)}>
                <CarouselContents
                  items={payload.banners.sort((a, b) => a.order - b.order)}
                  type='banner'
                  used='topbanner'
                  display='full'
                />
              </section>
            </div>
          )}

          {/* Categories Section */}
          {payload.recommendedCategoryMix && payload.recommendedCategoryMix.length > 0 && (
            <div className={clsx(styles.categoriesWrapper, styles.sectionOuterMargin)}>
              <div className={styles.sectionContents}>
                <div className={clsx(styles.contentWidth, styles.categoriesSection)}>
                  <TitleWithViewAll path='/categories' title='カテゴリ' />
                  <ul className={styles.categoryTagList}>
                    {extractedCategoryList.map((item) => (
                      <li key={item.id} className={styles.categoryTagItem}>
                        <Link href={`/categories/${item.slug}`} className={styles.articleTag}>
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                {payload.recommendedCategoryMix.map((categoryMix, index) => (
                  <section
                    key={categoryMix.stories?.[0]?.id || index}
                    className={clsx(styles.contentWidth, styles.categorySection)}
                  >
                    <TitleWithViewAll
                      path={categoryMixLink(categoryMix.tags)}
                      title={categoryMixTitle(categoryMix.tags)}
                    />
                    <div className={styles.categoryCards}>
                      {categoryMix.stories.slice(0, 1).map((story) => (
                        <ArticleCard
                          key={story.id}
                          article={{
                            ...story,
                            id: Number(story.id),
                            path: story.path || story.url || '#',
                            eyecatchImageUrl:
                              story.eyecatchImageUrl || '/images/article-placeholder.jpg',
                            openDate: story.openDate || story.publishedAt || '',
                            updatedAt: story.updatedAt || story.publishedAt || '',
                            title: story.title,
                            company: story.company
                              ? {
                                  id: Number(story.company.id),
                                  name: story.company.name,
                                  openUrl: story.company.openUrl || '',
                                  imageUrl: story.company.imageUrl || '',
                                  following: story.company.following || false,
                                }
                              : { id: 0, name: '', openUrl: '', imageUrl: '', following: false },
                            tags: story.tags || [],
                            storyTypeJa: story.storyTypeJa || 'article',
                            storyPeople: story.storyPeople || [],
                            isSaved: story.isSaved || false,
                          }}
                          used='top-category'
                        />
                      ))}
                      <div className={styles.smallContents}>
                        {categoryMix.stories.slice(1).map((story) => (
                          <ArticleCard
                            key={story.id}
                            article={{
                              ...story,
                              id: Number(story.id),
                              path: story.path || story.url || '#',
                              eyecatchImageUrl:
                                story.eyecatchImageUrl || '/images/article-placeholder.jpg',
                              openDate: story.openDate || story.publishedAt || '',
                              updatedAt: story.updatedAt || story.publishedAt || '',
                              title: story.title,
                              company: story.company
                                ? {
                                    id: Number(story.company.id),
                                    name: story.company.name,
                                    openUrl: story.company.openUrl || '',
                                    imageUrl: story.company.imageUrl || '',
                                    following: story.company.following || false,
                                  }
                                : { id: 0, name: '', openUrl: '', imageUrl: '', following: false },
                              tags: story.tags || [],
                              storyTypeJa: story.storyTypeJa || 'article',
                              storyPeople: story.storyPeople || [],
                              isSaved: story.isSaved || false,
                            }}
                            used='top-small-category'
                          />
                        ))}
                      </div>
                    </div>
                  </section>
                ))}
              </div>
            </div>
          )}

          {/* Editorial Section */}
          <div className={styles.sectionOuterMargin}>
            {payload.latestContentsByEditorialDept &&
              payload.latestContentsByEditorialDept.length > 0 && (
                <div className={clsx(styles.latestEditorialWrapper, styles.sectionContents)}>
                  <section className={clsx(styles.contentWidth, styles.editorialSection)}>
                    <div className={styles.carouselMarginContainer}>
                      <TitleWithViewAll
                        path='/talentbook_official'
                        title='talentbook編集部の最新記事'
                      />
                    </div>
                    <CarouselContents
                      items={payload.latestContentsByEditorialDept}
                      type='article'
                      display='full'
                    />
                  </section>
                </div>
              )}

            {/* Member Only Section */}
            {!getMemberId() && (
              <div className={clsx(styles.memberOnlyWrapper, styles.sectionSpacing)}>
                <section className={clsx(styles.contentWidth, styles.memberOnlySection)}>
                  <i className={clsx(styles.gradientCircle, styles.blue, styles.iconPos9)} />
                  <i className={clsx(styles.gradientCircle, styles.yellow, styles.iconPos8)} />
                  <i className={clsx(styles.gradientCircle, styles.redYellow, styles.iconPos7)} />
                  <i className={clsx(styles.gradientCircle, styles.redYellow, styles.iconPos6)} />
                  <i className={clsx(styles.gradientCircle, styles.blue, styles.iconPos5)} />
                  <i className={clsx(styles.gradientCircle, styles.yellow, styles.iconPos4)} />
                  <i className={clsx(styles.gradientCircle, styles.redYellow, styles.iconPos3)} />
                  <i className={clsx(styles.gradientCircle, styles.blue, styles.iconPos2)} />
                  <i className={clsx(styles.gradientCircle, styles.yellow, styles.iconPos1)} />
                  <div className={styles.memberOnlyHeader}>
                    <h2 className={styles.memberOnlyTitle}>
                      <span className={styles.memberTitleText}>会員限定機能で</span>
                      タレントブック・ストーリーをさらに便利に！
                    </h2>
                    <Link href='/signup' className={styles.signupBtn}>
                      会員登録
                    </Link>
                  </div>
                  <div className={styles.memberOnlyList}>
                    {memberOnlyContents.contents.map((item, i) => (
                      <dl key={i} className={styles.memberOnlyItem}>
                        <dt className={styles.memberOnlyPoint}>
                          <img
                            src={getImagePath(item.icon)}
                            width={80}
                            height={80}
                            alt={item.icon}
                            loading='lazy'
                          />
                        </dt>
                        <dd className={styles.memberOnlyContents}>
                          <img
                            src={getImagePath(item.pointImg)}
                            width={200}
                            height={40}
                            alt={`point${i + 1}`}
                            className={styles.pointContentsTitle}
                            loading='lazy'
                          />
                          <p className={styles.pointContentsText}>{item.text}</p>
                        </dd>
                      </dl>
                    ))}
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cookie Policy Dialog */}
      {isMounted && isOpenCookiePolicyDialog && (
        <CookiePolicyDialog onClose={() => setIsOpenCookiePolicyDialog(false)} />
      )}
    </div>
  )
}
